if (!customElements.get('tr-select')) {
class TrSelect extends HTMLElement {
	selectors = {
		button: '[data-tr-js-select-button]',
		native: '[data-tr-js-select-native]',
		options: '[data-tr-js-select-options]',
		option: '[data-tr-js-select-option]',
		value: '[data-tr-js-select-value]'
	};

	attributes = {
		expanded: 'aria-expanded',
		selected: 'aria-selected'
	};

	stateClasses = {
		isOpen: 'is-open',
		isSelected: 'is-selected',
		isMultiple: 'is-multiple',
		hasSelection: 'has-selection',
		isFree: 'is-free'
	};

	selectedValues = [];
	placeholderText = '';

	connectedCallback() {
		this.buttonElement = this.querySelector(this.selectors.button);
		this.nativeElement = this.querySelector(this.selectors.native);
		this.optionsElement = this.querySelector(this.selectors.options);
		this.optionElements = [...this.querySelectorAll(this.selectors.option)];
		this.valueElement = this.querySelector(this.selectors.value);

		this.init();
		this.bindEvents();
	}

	disconnectedCallback() {
		this.destroy();
	}

	init() {
		const emptyOption = this.optionElements.find((option) => option.dataset.value === '');

		this.placeholderText =
			emptyOption?.textContent.trim() || this.valueElement?.textContent.trim() || '';
		this.classList.toggle(this.stateClasses.isMultiple, this.isMultiple);

		if (this.isMultiple) {
			const defaultOption = [...this.nativeElement.options].find(
				(option) => option.hasAttribute('data-tr-js-default') && option.value
			);

			this.selectedValues = defaultOption ? [defaultOption.value] : [];
			this.syncNativeMultiple();
			this.syncMultipleSelection();
			this.syncEmptySubmitState();

			if (defaultOption) this.dispatchChange();
			return;
		}

		this.updateSelectedOption(this.nativeElement.value);
		this.syncEmptySubmitState();
	}

	bindEvents() {
		this.buttonElement.addEventListener('click', this.onButtonClick);
		this.buttonElement.addEventListener('keydown', this.onButtonKeydown);
		this.optionsElement.addEventListener('click', this.onOptionsClick);
		document.addEventListener('click', this.onDocumentClick);
	}

	onButtonClick = () => {
		this.toggle();
	};

	onButtonKeydown = (event) => {
		if (event.key === 'Escape') {
			this.close();
		}

		if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			this.open();
		}
	};

	onOptionsClick = (event) => {
		const optionElement = event.target.closest(this.selectors.option);

		if (optionElement == null) return;

		this.select(optionElement.dataset.value);
	};

	onDocumentClick = (event) => {
		if (!this.contains(event.target)) this.close();
	};

	toggle() {
		if (this.classList.contains(this.stateClasses.isOpen)) {
			this.close();
		} else {
			this.open();
		}
	}

	open() {
		this.classList.add(this.stateClasses.isOpen);
		this.buttonElement.setAttribute(this.attributes.expanded, 'true');
	}

	close() {
		this.classList.remove(this.stateClasses.isOpen);
		this.buttonElement.setAttribute(this.attributes.expanded, 'false');
	}

	select(value) {
		if (this.isMultiple) {
			this.toggleMultipleValue(value);
			return;
		}

		if (value === this.nativeElement.value) {
			this.close();
			return;
		}

		this.nativeElement.value = value;
		this.updateSelectedOption(value);
		this.syncEmptySubmitState();
		this.dispatchChange();
		this.close();
	}

	toggleMultipleValue(value) {
		if (value === '') {
			this.selectedValues = [];
			this.syncNativeMultiple();
			this.syncMultipleSelection();
			this.syncEmptySubmitState();
			this.dispatchChange();
			this.close();
			return;
		}

		const selectedIndex = this.selectedValues.indexOf(value);

		if (selectedIndex === -1) {
			this.selectedValues.push(value);
		} else {
			this.selectedValues.splice(selectedIndex, 1);
		}

		this.syncNativeMultiple();
		this.syncMultipleSelection();
		this.syncEmptySubmitState();
		this.dispatchChange();
	}

	syncNativeMultiple() {
		[...this.nativeElement.options].forEach((option) => {
			option.selected = this.selectedValues.includes(option.value);
		});
	}

	syncMultipleSelection() {
		const hasSelection = this.selectedValues.length > 0;
		const firstSelectedValue = this.selectedValues[0] || '';

		this.classList.toggle(this.stateClasses.hasSelection, hasSelection);

		this.optionElements.forEach((optionElement) => {
			const value = optionElement.dataset.value;
			const isEmpty = value === '';
			const isSelected = isEmpty ? !hasSelection : this.selectedValues.includes(value);
			const isFree = isEmpty ? false : !hasSelection || value === firstSelectedValue;

			optionElement.classList.toggle(this.stateClasses.isSelected, isSelected);
			optionElement.classList.toggle(this.stateClasses.isFree, isFree);
			optionElement.setAttribute(this.attributes.selected, String(isSelected));
		});

		this.updateMultipleValueLabel();
	}

	updateMultipleValueLabel() {
		if (!this.selectedValues.length) {
			this.valueElement.textContent = this.placeholderText;
			return;
		}

		const labels = this.selectedValues
			.map((value) => {
				const optionElement = this.optionElements.find((option) => option.dataset.value === value);

				return optionElement?.getAttribute('data-tr-js-label') || '';
			})
			.filter(Boolean);

		this.valueElement.textContent = labels.join(', ');
	}

	updateSelectedOption(value) {
		if (this.isMultiple) {
			this.syncMultipleSelection();
			return;
		}

		this.optionElements.forEach((optionElement) => {
			const isSelected = optionElement.dataset.value === value;

			optionElement.classList.toggle(this.stateClasses.isSelected, isSelected);
			optionElement.setAttribute(this.attributes.selected, String(isSelected));

			if (isSelected) this.valueElement.innerHTML = optionElement.innerHTML.trim();
		});
	}

	dispatchChange() {
		this.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
		this.nativeElement.dispatchEvent(new Event('change', { bubbles: true }));
	}

	getSelectedValues() {
		if (this.isMultiple) return [...this.selectedValues];

		return this.nativeElement?.value ? [this.nativeElement.value] : [];
	}

	syncEmptySubmitState() {
		if (!this.nativeElement.hasAttribute('data-tr-js-omit-empty')) return;

		this.nativeElement.disabled = this.isMultiple
			? this.selectedValues.length === 0
			: this.nativeElement.value === '';
	}

	get isMultiple() {
		return Boolean(this.nativeElement?.multiple);
	}

	destroy() {
		this.buttonElement.removeEventListener('click', this.onButtonClick);
		this.buttonElement.removeEventListener('keydown', this.onButtonKeydown);
		this.optionsElement.removeEventListener('click', this.onOptionsClick);
		document.removeEventListener('click', this.onDocumentClick);
	}
}

	customElements.define('tr-select', TrSelect);
}
